import { registerDecorators as _registerDecorators, registerComponent as _registerComponent, LightningElement } from "lwc";
import _tmpl from "./pricingV2.html";
import { WireStore } from '@oneappexchange/lwc-wired';
import { getPlanPrice, getPlanUnits, hasDiscountNonProfits, isFreemiumListing, isFreeToInstallListing, isLowestStartingPrice, isPaidListing, isPaidWithCheckout, SFID_REGEX } from '~/modules/helpers/pricingHelper';
import { i18nStore, WireI18n } from '~/modules/stores/i18nStore';
import { routerStore, selectScrollToQueryParam } from '~/modules/stores/routerStore';
import { scrollToElement } from '~/modules/utilities/browser';
import { buildFrequencyLabels, buildUnitLabels, capitalizeWords } from '~/modules/utilities/pricingUtilities';
/** "Pricing" tab panel: reads `listing.pricing` and renders a `listing-pricing-card` per plan. */
class PricingV2 extends LightningElement {
  constructor(...args) {
    super(...args);
    this.listing = void 0;
    // The buy-box "Pricing Details" link deep-links to this tab with
    // ?scrollTo=pricing-details (see listingDetailV2.handlePricingSummaryLinkClick).
    // v2 must consume it here — v1's `listing-pricing` did, but the pricing panel was
    // forked into `listing-pricing-v2` without carrying the wire over.
    this.scrollToParam = void 0;
  }
  i18nCallback(i18n) {
    this.staticLabels = i18n.listingDetails.pricing;
    this.frequencyLabels = buildFrequencyLabels(this.staticLabels);
    this.unitLabels = buildUnitLabels(this.staticLabels);
  }
  renderedCallback() {
    // moveFocus: land the screen-reader/keyboard reading position on the pricing
    // heading (see data-scroll-to in the template) so the section is announced,
    // not just scrolled into view. Mirrors listing-achievements. No-ops when the
    // param is absent.
    scrollToElement(this, this.scrollToParam, true);
  }
  get pricing() {
    // @ts-expect-error * Automated Type Error Fix
    return this.listing?.pricing;
  }
  get paidModelPlans() {
    return this.pricing?.model?.plans ?? [];
  }
  get hasPlans() {
    return this.paidModelPlans.length > 0;
  }

  /**
   * Freemium / free-to-install listings — the free-model variants that still get
   * a Pricing tab (strictly-`free` listings no longer render this panel; see
   * listingDetailV2's showPricingTab). Ported from v1's hasSubSectionHeader.
   */
  get isFreeModelListing() {
    return isFreemiumListing(this.pricing) || isFreeToInstallListing(this.pricing);
  }

  // Guards the heading so we don't show it when there's nothing to display.
  // Note: requirements/limitations text only exists on freemium/free-to-install
  // listings, which isFreeModelListing already covers. Additional details can
  // appear on a paid listing with no plans; likewise a plan-less paid listing that
  // offers nonprofit discounts has a pricing description ("Discounts for
  // nonprofits.") but no plans/free-model flag, so hasPricingDescription keeps
  // that messaging reachable (a plan-less paid listing with no discounts has an
  // empty description and stays hidden).
  get hasDisplayableContent() {
    return this.hasPlans || this.isFreeModelListing || this.hasAdditionalDetails || this.hasPricingDescription;
  }

  /**
   * The Pricing tab heading — the plan type ("Freemium", "Paid", or "Paid Add-On
   * Required"), with a "| Discounts for nonprofits" suffix when the listing offers
   * nonprofit discounts. Replaces the former static "Pricing" heading. Non-empty
   * whenever hasDisplayableContent is true (freemium/free-to-install/paid).
   * Freemium/free-to-install ported from v1's subSectionHeader; the paid header is
   * v2-only (v1 renders no paid header).
   */
  get heading() {
    const nonProfit = hasDiscountNonProfits(this.pricing);
    if (isFreemiumListing(this.pricing)) {
      return nonProfit ? this.staticLabels?.freemiumSubSectionHeaderNonProfit ?? '' : this.staticLabels?.freemiumSubSectionHeader ?? '';
    }
    if (isFreeToInstallListing(this.pricing)) {
      return nonProfit ? this.staticLabels?.freeToInstallSubSectionHeaderNonProfit ?? '' : this.staticLabels?.freeToInstallSubSectionHeader ?? '';
    }
    if (isPaidListing(this.pricing)) {
      return nonProfit ? this.staticLabels?.paidSubSectionHeaderNonProfit ?? '' : this.staticLabels?.paidSubSectionHeader ?? '';
    }
    return '';
  }

  /**
   * Description under the heading — the per-model pricing-details copy, swapping
   * to the nonprofit-discount variant when the listing offers discounts. Paid
   * listings have no base description and show text only when they offer nonprofit
   * discounts. Ported from v1's pricingDetailsDescription.
   */
  get pricingDescription() {
    const nonProfit = hasDiscountNonProfits(this.pricing);
    if (isFreemiumListing(this.pricing)) {
      return nonProfit ? this.staticLabels?.nonProfitDetailsDescriptionFreemium ?? '' : this.staticLabels?.detailsDescriptionFreemium ?? '';
    }
    if (isFreeToInstallListing(this.pricing)) {
      return nonProfit ? this.staticLabels?.nonProfitDetailsDescriptionFreeToInstall ?? '' : this.staticLabels?.detailsDescriptionFreeToInstall ?? '';
    }
    if (isPaidListing(this.pricing)) {
      return nonProfit ? this.staticLabels?.detailsDescriptionNonProfitsAndPayment ?? '' : '';
    }
    return '';
  }
  get hasPricingDescription() {
    return Boolean(this.pricingDescription);
  }

  /** Publisher-authored free-text; ported from v1's pricingCustomDetailsDescription. */
  get additionalDetails() {
    const model = this.pricing?.model;
    return model?.additional_details ?? '';
  }
  get hasAdditionalDetails() {
    return Boolean(this.additionalDetails);
  }

  /**
   * Freemium's `user_limitations` and Free-to-Install's `additional_services` are mutually
   * exclusive by model; ported from v1's pricingRequirementsAndLimitation.
   */
  get requirementsAndLimitationsText() {
    if (isFreemiumListing(this.pricing)) {
      return this.pricing?.model?.user_limitations ?? '';
    }
    if (isFreeToInstallListing(this.pricing)) {
      return this.pricing?.model?.additional_services ?? '';
    }
    return '';
  }
  get hasRequirementsAndLimitationsText() {
    return Boolean(this.requirementsAndLimitationsText);
  }
  get plans() {
    const displayCheckoutPlanName = isPaidWithCheckout(this.pricing) && this.paidModelPlans.length > 1;
    const plans = [...this.paidModelPlans];
    return plans.sort((a, b) => a.index - b.index).map((plan, index) => ({
      index: plan.index,
      planName: this.getPlanName(plan.plan_name, index + 1, displayCheckoutPlanName, plans),
      formattedPrice: getPlanPrice(plan.currency_code, plan.price),
      unitFrequencyLabel: this.getUnitFrequencyLabel(plan),
      showLowestStartingPriceBadge: isLowestStartingPrice(plan),
      trialPeriodLabel: this.getTrialPeriodLabel(plan),
      centered: false
    }));
  }

  /** Cards to render in the grid: the paid/plan-bearing plans (empty for plan-less free models). */
  get cards() {
    return this.plans;
  }
  get hasCards() {
    return this.cards.length > 0;
  }
  getUnitFrequencyLabel(plan) {
    const units = this.getPlanUnitsLabel(plan);
    const frequency = this.getPlanFrequencyLabel(plan.frequency);
    if (!units && !frequency) {
      return '';
    }
    // Title-case the unit/cadence (e.g. `user/month` -> `User/Month`) to match
    // the pricing summary's presentation (pricingSummaryV2).
    return capitalizeWords([units, frequency].filter(Boolean).join('/'));
  }

  /**
   * Localizes the standard `user`/`company` units; falls back to the raw value
   * for `custom` units, which carry publisher free-text (`custom_unit`) we don't
   * translate. getPlanUnits already resolves `custom` to that free-text string.
   */
  getPlanUnitsLabel(plan) {
    const units = getPlanUnits(plan);
    if (units === 'user' || units === 'company') {
      return this.unitLabels?.[units] ?? units;
    }
    return units;
  }
  getPlanFrequencyLabel(frequency) {
    // @ts-expect-error * Automated Type Error Fix
    return this.frequencyLabels?.[frequency] ?? '';
  }
  getTrialPeriodLabel(plan) {
    if (plan.trial_days == null || plan.trial_days <= 0) {
      return '';
    }
    return i18nStore.getState().i18n.listingDetails.pricing.trialPeriodDescriptionV2({
      TRIAL_PERIOD: String(plan.trial_days)
    });
  }

  /**
   * Falls back to a numbered default name when `name` looks like a raw SFID or is empty, and
   * suppresses the raw `plan_name` for checkout listings unless the publisher opted into custom
   * names (`display_plan_names`). Ported from v1's getPlanName/isPaidWithCheckout handling.
   */
  getPlanName(name, index, displayCheckoutPlanName, plans) {
    const pricing = i18nStore.getState().i18n.listingDetails.pricing;
    const paidWithCheckout = isPaidWithCheckout(this.pricing);
    const displayCustomPlanNames = this.pricing?.model?.display_plan_names;
    if (!displayCheckoutPlanName) {
      if (SFID_REGEX.test(name) || paidWithCheckout) {
        return plans.length > 1 ? pricing.defaultPlanNames({
          VALUE: index
        }) : pricing.defaultPlanName();
      }
      return name || pricing.defaultPlanName();
    }
    if (displayCustomPlanNames) {
      return name;
    }
    return pricing.checkoutPlanName({
      VALUE: index
    });
  }
  /*LWC compiler v7.1.3*/
}
_registerDecorators(PricingV2, {
  publicProps: {
    listing: {
      config: 0
    }
  },
  wire: {
    i18nCallback: {
      adapter: WireI18n,
      method: 1,
      config: function ($cmp) {
        return {};
      }
    },
    scrollToParam: {
      adapter: WireStore,
      dynamic: [],
      config: function ($cmp) {
        return {
          store: routerStore,
          selector: selectScrollToQueryParam
        };
      }
    }
  }
});
const __lwc_component_class_internal = _registerComponent(PricingV2, {
  tmpl: _tmpl,
  sel: "",
  apiVersion: 62
});
export default __lwc_component_class_internal;